import { useTables, useUpdateTable } from '@/features/reservation-setup/hooks/useReservationSetup';
import { useT } from '@/i18n';
import {
  Block,
  List,
  ListInput,
  ListItem,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useState } from 'react';

// A fully React-controlled +/− stepper. We don't use F7's <Stepper> (a
// "framework component" that initialises itself imperatively and can clobber
// React state on mount) - this one has no such timing dependency.
function NumberStepper({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const rs = useT().reservationSetup;
  return (
    <span className="num-stepper">
      <button
        type="button"
        className="num-stepper-btn"
        aria-label={rs.decrease}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <span className="num-stepper-value">{value}</span>
      <button
        type="button"
        className="num-stepper-btn"
        aria-label={rs.increase}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
    </span>
  );
}

/**
 * Edit a single table. This used to be a PushSheet inside AdvancedTablesPage,
 * now a classic pushed router page (Rule: a self-contained edit flow is a pushed
 * page, not a modal sheet - same migration as ExceptionFormPage). Reached from
 * the Tables list via `/settings/reservation/advanced-tables/:tableId`.
 */
export default function TableEditFormPage({
  f7route,
  f7router,
}: {
  f7route: { params: { tableId?: string } };
  f7router: { back: () => void };
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const { tableId } = f7route.params;
  const { data: tables } = useTables();
  const updateTable = useUpdateTable();
  const table = tables?.find((tbl) => tbl.id === tableId);

  const [label, setLabel] = useState('');
  const [seats, setSeats] = useState(2);
  const [isActive, setIsActive] = useState(true);
  const [countsForCapacity, setCountsForCapacity] = useState(true);
  const [isBookableOnline, setIsBookableOnline] = useState(true);
  const [seeded, setSeeded] = useState(false);

  // Seed the form once the table resolves (the list may load after a deep link).
  useEffect(() => {
    if (seeded || !table) return;
    setLabel(table.label ?? '');
    setSeats(table.seats);
    setIsActive(table.isActive);
    setCountsForCapacity(table.countsForCapacity);
    setIsBookableOnline(table.isBookableOnline);
    setSeeded(true);
  }, [seeded, table]);

  const back = () => f7router.back();

  const submit = () => {
    if (!table) return;
    updateTable.mutate(
      {
        tableId: table.id,
        body: {
          label: label.trim() || undefined,
          seats,
          isActive,
          countsForCapacity,
          isBookableOnline,
        },
      },
      {
        onSuccess: () => {
          f7.toast.show({ text: rs.tableUpdated, closeTimeout: 2000 });
          back();
        },
        onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.saveFailed),
      },
    );
  };

  const computedLabel = table ? `${seats}p · #${table.ordinal}` : '';
  // Deep-linked before the list loaded, or an unknown id.
  const loading = !tables;

  return (
    <Page name="table-edit">
      <Navbar>
        <NavLeft>
          <F7Link onClick={back}>{t.cancel}</F7Link>
        </NavLeft>
        <NavTitle>{rs.editTable}</NavTitle>
        <NavRight>
          <F7Link onClick={table ? submit : undefined}>{t.saveShort}</F7Link>
        </NavRight>
      </Navbar>

      {loading && (
        <Block className="text-align-center" style={{ paddingTop: 48 }}>
          <Preloader />
        </Block>
      )}

      {!loading && !table && (
        <Block strongIos strongMd outlineIos className="text-align-center">
          <p>{rs.tableGone}</p>
        </Block>
      )}

      {!loading && table && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.labelOverride}
              placeholder={computedLabel}
              value={label}
              onInput={(e) => setLabel((e.target as HTMLInputElement).value.slice(0, 40))}
              clearButton
              info={rs.useAutoGenerated}
            />
            <ListItem noChevron title={rs.seats}>
              <div slot="after">
                <NumberStepper value={seats} min={1} max={30} onChange={setSeats} />
              </div>
            </ListItem>
          </List>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem
              checkbox
              title={rs.active}
              checked={isActive}
              onChange={(e) => setIsActive((e.target as HTMLInputElement).checked)}
            />
            <ListItem
              checkbox
              title={rs.countsForCapacity}
              checked={countsForCapacity}
              onChange={(e) => setCountsForCapacity((e.target as HTMLInputElement).checked)}
            />
            <ListItem
              checkbox
              title={rs.bookableOnline}
              checked={isBookableOnline}
              onChange={(e) => setIsBookableOnline((e.target as HTMLInputElement).checked)}
            />
          </List>
        </>
      )}
    </Page>
  );
}
